import { useState, useEffect } from "react"
import { Search, UserX, UserCheck, Eye, X, MoreVertical, Shield, UserCog } from "lucide-react"
import {
  useGetUsersQuery,
  useGetUserByIdQuery,
  useSuspendUserMutation,
  useGetUserRolesQuery,
  useGetRolesQuery,
  useAssignRoleMutation,
  useRevokeRoleMutation
} from "../features/admin/adminApi"

export default function UsersPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState("")
  const [searchInput, setSearchInput] = useState("")
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set())
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null)
  const [showRolesModal, setShowRolesModal] = useState(false)
  const [rolesUserId, setRolesUserId] = useState<string | null>(null)
  const [showAssignRoleModal, setShowAssignRoleModal] = useState(false)
  const [assignRoleUserId, setAssignRoleUserId] = useState<string | null>(null)
  const [selectedRoleId, setSelectedRoleId] = useState("")
  const [openMenuUserId, setOpenMenuUserId] = useState<string | null>(null)

  const { data, isLoading } = useGetUsersQuery({ page, search })
  const { data: userDetails, isLoading: isLoadingDetails } = useGetUserByIdQuery(selectedUserId!, {
    skip: !selectedUserId,
  })
  const { data: userRolesData } = useGetUserRolesQuery(rolesUserId!, {
    skip: !rolesUserId,
  })
  const { data: rolesData } = useGetRolesQuery()
  const [suspendUser] = useSuspendUserMutation()
  const [assignRole] = useAssignRoleMutation()
  const [revokeRole] = useRevokeRoleMutation()

  const users = data?.users ?? []
  const totalPages = data?.totalPages ?? 1
  const userRoles = userRolesData?.roles ?? []
  const availableRoles = rolesData ?? []

  // Clear selected users and close menus when page or search changes
  useEffect(() => {
    setSelectedUsers(new Set())
    setOpenMenuUserId(null)
  }, [page, search])

  const handleSuspend = async (uid: string, currentlyActive: boolean) => {
    const action = currentlyActive ? "suspend" : "activate"
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} this user?`)) return
    await suspendUser({ uid, suspend: currentlyActive })
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedUsers(new Set(users.filter(u => u.role !== "admin").map(u => u.id)))
    } else {
      setSelectedUsers(new Set())
    }
  }

  const handleSelectUser = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedUsers)
    if (checked) {
      newSelected.add(id)
    } else {
      newSelected.delete(id)
    }
    setSelectedUsers(newSelected)
  }

  const handleBulkSuspend = async () => {
    if (selectedUsers.size === 0) return
    if (!confirm(`Suspend ${selectedUsers.size} user(s)?`)) return

    try {
      await Promise.all(
        Array.from(selectedUsers).map(uid => suspendUser({ uid, suspend: true }).unwrap())
      )
      setSelectedUsers(new Set())
      alert("Users suspended successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to suspend users")
    }
  }

  const handleBulkActivate = async () => {
    if (selectedUsers.size === 0) return
    if (!confirm(`Activate ${selectedUsers.size} user(s)?`)) return

    try {
      await Promise.all(
        Array.from(selectedUsers).map(uid => suspendUser({ uid, suspend: false }).unwrap())
      )
      setSelectedUsers(new Set())
      alert("Users activated successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to activate users")
    }
  }

  const handleAssignRole = async () => {
    if (!assignRoleUserId || !selectedRoleId) return

    try {
      await assignRole({ uid: assignRoleUserId, roleUid: selectedRoleId }).unwrap()
      setShowAssignRoleModal(false)
      setAssignRoleUserId(null)
      setSelectedRoleId("")
      alert("Role assigned successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to assign role")
    }
  }

  const handleRevokeRole = async (uid: string, roleUid: string) => {
    if (!confirm("Revoke this role?")) return

    try {
      await revokeRole({ uid, roleUid }).unwrap()
      alert("Role revoked successfully")
    } catch (err: any) {
      alert(err?.data?.message || "Failed to revoke role")
    }
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Users</h1>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {/* Search bar & Bulk Actions */}
        <div className="p-4 border-b border-gray-100">
          <form
            onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); setPage(1) }}
            className="flex gap-2 mb-3"
          >
            <div className="relative flex-1 max-w-xs">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search by name or email..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700">
              Search
            </button>
            {search && (
              <button
                type="button"
                onClick={() => { setSearch(""); setSearchInput(""); setPage(1) }}
                className="px-3 py-2 border border-gray-300 text-sm rounded-lg hover:bg-gray-50"
              >
                Clear
              </button>
            )}
          </form>

          {/* Bulk Actions Bar */}
          {selectedUsers.size > 0 && (
            <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
              <span className="text-sm text-blue-900 font-medium">
                {selectedUsers.size} user(s) selected
              </span>
              <div className="flex gap-2">
                <button
                  onClick={handleBulkActivate}
                  className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition flex items-center gap-1"
                >
                  <UserCheck size={14} />
                  Activate
                </button>
                <button
                  onClick={handleBulkSuspend}
                  className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition flex items-center gap-1"
                >
                  <UserX size={14} />
                  Suspend
                </button>
              </div>
            </div>
          )}
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading users...</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="px-4 py-3 w-12">
                    <input
                      type="checkbox"
                      checked={selectedUsers.size > 0 && selectedUsers.size === users.filter(u => u.role !== "admin").length}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Email</th>
                  <th className="px-4 py-3 text-left">Phone</th>
                  <th className="px-4 py-3 text-left">Role</th>
                  <th className="px-4 py-3 text-left">Status</th>
                  <th className="px-4 py-3 text-left">Joined</th>
                  <th className="px-4 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.length === 0 ? (
                  <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No users found.</td></tr>
                ) : (
                  users.map((u) => (
                    <tr key={u.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        {u.role !== "admin" && (
                          <input
                            type="checkbox"
                            checked={selectedUsers.has(u.id)}
                            onChange={(e) => handleSelectUser(u.id, e.target.checked)}
                            className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                          />
                        )}
                      </td>
                      <td className="px-4 py-3 font-medium text-gray-900">{u.firstName} {u.lastName}</td>
                      <td className="px-4 py-3 text-gray-600">{u.email}</td>
                      <td className="px-4 py-3 text-gray-600">{u.phone || "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${u.role === "admin" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-700"}`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${u.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                          {u.isActive ? "Active" : "Suspended"}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{new Date(u.createdAt).toLocaleDateString()}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setSelectedUserId(u.id)}
                            title="View Details"
                            className="p-1.5 rounded-lg transition-colors text-blue-600 hover:bg-blue-50"
                          >
                            <Eye size={16} />
                          </button>
                          {u.role !== "admin" && (
                            <>
                              <button
                                onClick={() => handleSuspend(u.id, u.isActive)}
                                title={u.isActive ? "Suspend user" : "Activate user"}
                                className={`p-1.5 rounded-lg transition-colors ${u.isActive ? "text-red-500 hover:bg-red-50" : "text-green-600 hover:bg-green-50"}`}
                              >
                                {u.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                              </button>
                              <div className="relative">
                                <button
                                  onClick={() => setOpenMenuUserId(openMenuUserId === u.id ? null : u.id)}
                                  className="p-1.5 rounded-lg transition-colors text-gray-600 hover:bg-gray-100"
                                  title="More Actions"
                                >
                                  <MoreVertical size={16} />
                                </button>
                                {openMenuUserId === u.id && (
                                  <>
                                    {/* Backdrop to close menu when clicking outside */}
                                    <div
                                      className="fixed inset-0 z-10"
                                      onClick={() => setOpenMenuUserId(null)}
                                    />
                                    <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
                                      <button
                                        onClick={() => {
                                          setRolesUserId(u.id)
                                          setShowRolesModal(true)
                                          setOpenMenuUserId(null)
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 rounded-t-lg"
                                      >
                                        <Shield size={14} />
                                        View Roles
                                      </button>
                                      <button
                                        onClick={() => {
                                          setAssignRoleUserId(u.id)
                                          setShowAssignRoleModal(true)
                                          setOpenMenuUserId(null)
                                        }}
                                        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 rounded-b-lg"
                                      >
                                        <UserCog size={14} />
                                        Assign Role
                                      </button>
                                    </div>
                                  </>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between text-sm text-gray-500">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-3 py-1.5 border border-gray-300 rounded-lg disabled:opacity-40 hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* User Details Modal */}
      {selectedUserId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">User Details</h2>
              <button
                onClick={() => setSelectedUserId(null)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            {isLoadingDetails ? (
              <div className="p-8 text-center text-gray-500">Loading details...</div>
            ) : userDetails ? (
              <div className="p-6 space-y-6">
                {/* User Info */}
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    {userDetails.firstName} {userDetails.lastName}
                  </h3>
                  <div className="flex gap-2 mb-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${userDetails.isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                      {userDetails.isActive ? "Active" : "Suspended"}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${userDetails.isVerified ? "bg-blue-100 text-blue-700" : "bg-yellow-100 text-yellow-700"}`}>
                      {userDetails.isVerified ? "Verified" : "Not Verified"}
                    </span>
                    <span className="px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-700 capitalize">
                      {userDetails.role}
                    </span>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold text-gray-900 mb-3">Contact Information</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Email:</span>
                      <p className="font-medium text-gray-900">{userDetails.email}</p>
                    </div>
                    {userDetails.phone && (
                      <div>
                        <span className="text-gray-500">Phone:</span>
                        <p className="font-medium text-gray-900">{userDetails.phone}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Businesses */}
                {userDetails.businesses && userDetails.businesses.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Businesses ({userDetails.businesses.length})</h4>
                    <div className="space-y-2">
                      {userDetails.businesses.map((business) => (
                        <div key={business.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{business.name}</p>
                            <p className="text-xs text-gray-500">
                              Created {new Date(business.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="flex gap-1">
                            {business.isVerified && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
                                Verified
                              </span>
                            )}
                            {business.isFeatured && (
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700">
                                Featured
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Orders */}
                {userDetails.orders && userDetails.orders.length > 0 && (
                  <div className="border-t pt-4">
                    <h4 className="font-semibold text-gray-900 mb-3">Recent Orders ({userDetails.orders.length})</h4>
                    <div className="space-y-2">
                      {userDetails.orders.map((order) => (
                        <div key={order.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-gray-900">{order.orderNumber}</p>
                            <p className="text-xs text-gray-500">
                              {new Date(order.createdAt).toLocaleDateString()}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-semibold text-gray-900">₦{order.totalAmount.toLocaleString()}</p>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${order.isPaid ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                              {order.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Metadata */}
                <div className="border-t pt-4 text-xs text-gray-500 space-y-1">
                  <p>Created: {new Date(userDetails.createdAt).toLocaleString()}</p>
                  <p>Last Updated: {new Date(userDetails.updatedAt).toLocaleString()}</p>
                  <p>User ID: <span className="font-mono">{userDetails.id}</span></p>
                </div>
              </div>
            ) : (
              <div className="p-8 text-center text-red-500">Failed to load user details</div>
            )}
          </div>
        </div>
      )}

      {/* View Roles Modal */}
      {showRolesModal && rolesUserId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">User Roles</h2>
              <button
                onClick={() => {
                  setShowRolesModal(false)
                  setRolesUserId(null)
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              {userRoles.length === 0 ? (
                <p className="text-center text-gray-500 py-8">No roles assigned</p>
              ) : (
                <div className="space-y-2">
                  {userRoles.map((role) => (
                    <div key={role.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-medium text-gray-900">{role.name}</p>
                        {role.description && (
                          <p className="text-sm text-gray-500">{role.description}</p>
                        )}
                      </div>
                      <button
                        onClick={() => handleRevokeRole(rolesUserId, role.id)}
                        className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 rounded-lg transition"
                      >
                        Revoke
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Assign Role Modal */}
      {showAssignRoleModal && assignRoleUserId && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full">
            <div className="border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h2 className="text-xl font-bold text-gray-900">Assign Role</h2>
              <button
                onClick={() => {
                  setShowAssignRoleModal(false)
                  setAssignRoleUserId(null)
                  setSelectedRoleId("")
                }}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Role
              </label>
              <select
                value={selectedRoleId}
                onChange={(e) => setSelectedRoleId(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">-- Select a role --</option>
                {availableRoles.map((role) => (
                  <option key={role.id} value={role.id}>
                    {role.name}
                  </option>
                ))}
              </select>

              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => {
                    setShowAssignRoleModal(false)
                    setAssignRoleUserId(null)
                    setSelectedRoleId("")
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleAssignRole}
                  disabled={!selectedRoleId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Assign Role
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
